/* Copyright (c) 2020-present CloudIO Inc */

import {
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import useDebounce from 'hooks/useDebounce';
import Icon, { parseIconText } from 'icons/Icon';
import React, { startTransition, useRef, useState } from 'react';

export function WizardOption({
  iconName,
  title,
  description,
  onClick,
  'data-testid': testId,
}: {
  iconName: string;
  title: string;
  description: string;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  'data-testid': string;
}) {
  const icon = parseIconText(iconName);

  return (
    <Paper
      data-testid={`io-wo-${testId}`}
      onClick={onClick}
      elevation={3}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 2,
        flexDirection: 'row',
        padding: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        minHeight: 92,
      }}
    >
      <Box
        sx={{
          bgcolor: 'divider',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 60,
            height: 60,
            bgcolor: 'primary.main',
            margin: 2,
          }}
        >
          <Icon
            width={32}
            height={32}
            iconName={icon?.iconName}
            iconType={icon?.iconType}
            parentColor="primary"
          />
        </Avatar>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: 2,
        }}
      >
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      </Box>
    </Paper>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 8px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: '4px 16px',
  },
}));

export function SearchField({
  value = '',
  onChange,
  tip,
  placeholder,
  'data-testid': testId,
}: {
  value?: string;
  onChange: (v: string) => void;
  tip?: string;
  placeholder?: string;
  'data-testid': string;
}) {
  const classes = useStyles();
  const ref = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState(value);
  const [debounce] = useDebounce((v) => {
    startTransition(() => {
      onChange(v.toLowerCase());
    });
  });
  return (
    <div className={classes.root}>
      <IconButton
        onClick={() => {
          ref.current?.focus();
        }}
        className={classes.iconButton}
        aria-label="Search"
      >
        <Icon iconName="search" />
      </IconButton>
      <InputBase
        data-testid={testId}
        inputRef={ref}
        className={classes.input}
        placeholder={placeholder ?? 'Filter...'}
        inputProps={{ 'aria-label': placeholder || 'filter' }}
        value={filter ?? ''}
        onChange={(e) => {
          setFilter(e.target.value);
          debounce(300, e.target.value);
        }}
      />
      {tip ? (
        <Divider className={classes.divider} orientation="vertical" />
      ) : null}
      {tip ? (
        <Typography color="textSecondary" variant="body2">
          {tip}
        </Typography>
      ) : null}
    </div>
  );
}
